import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SITE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", organization: "", message: "" };

export const SignupForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can stay in touch.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/signups`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        message: form.message.trim() || null,
      });
      setSuccess(true);
      setForm(initial);
      toast.success("Thank you — your name has joined the candle.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="join"
      className="relative py-24 sm:py-32 bg-background bg-grain"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-primary/90 mb-5">
            Join the Release List
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] tracking-tight text-balance">
            Be there when
            <br />
            the candle is <span className="italic text-primary">lit</span>.
          </h2>
          <div className="gold-rule w-24 my-8" />
          <p className="text-foreground/75 leading-relaxed text-base sm:text-lg max-w-md">
            For artists, communities, donors, media, and potential partners —
            leave your name and we&apos;ll be in touch as the song moves toward
            release.
          </p>

          <ul className="mt-10 space-y-3 text-sm text-foreground/55">
            {[
              "Artists & musicians",
              "Synagogues, churches & community groups",
              "Donors & cultural sponsors",
              "Media & press inquiries",
              "Potential partners",
            ].map((line) => (
              <li key={line} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <form
          data-testid={SITE.signupForm}
          onSubmit={onSubmit}
          className="space-y-8"
        >
          <div>
            <label className="block text-[11px] uppercase tracking-[0.28em] text-foreground/55 mb-2">
              Name
            </label>
            <input
              data-testid={SITE.signupName}
              type="text"
              required
              value={form.name}
              onChange={onChange("name")}
              placeholder="Your full name"
              className="input-elegant w-full"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.28em] text-foreground/55 mb-2">
              Email
            </label>
            <input
              data-testid={SITE.signupEmail}
              type="email"
              required
              value={form.email}
              onChange={onChange("email")}
              placeholder="you@example.com"
              className="input-elegant w-full"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.28em] text-foreground/55 mb-2">
              Organization <span className="text-foreground/30">(optional)</span>
            </label>
            <input
              data-testid={SITE.signupOrg}
              type="text"
              value={form.organization}
              onChange={onChange("organization")}
              placeholder="Synagogue, label, outlet, community…"
              className="input-elegant w-full"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.28em] text-foreground/55 mb-2">
              Message <span className="text-foreground/30">(optional)</span>
            </label>
            <textarea
              data-testid={SITE.signupMessage}
              value={form.message}
              onChange={onChange("message")}
              rows={4}
              placeholder="A word, a wish, a way you'd like to be part of this…"
              className="input-elegant w-full resize-none"
            />
          </div>

          <div className="pt-4">
            <Button
              data-testid={SITE.signupSubmit}
              type="submit"
              disabled={loading}
              className="btn-primary rounded-none h-12 px-10 text-sm tracking-[0.18em] uppercase font-medium disabled:opacity-60"
            >
              {loading ? "Lighting…" : "Light Your Candle With Us"}
            </Button>

            {success && (
              <p
                data-testid={SITE.signupSuccess}
                className="mt-6 text-sm text-primary font-serif italic"
              >
                Thank you. Your name has joined the candle.
              </p>
            )}

            <div className="mt-10 flex flex-wrap gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-foreground/45">
              <span className="text-primary/80">#Shabbat250</span>
              <span className="text-primary/80">#ShabbatShalomAmerica</span>
              <span className="text-primary/80">#LightYourCandle</span>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};
